/**
 * Widerrufsbelehrung — § 312g BGB
 *
 * Standard B2C distance-selling template adapted for digital downloads
 * (the § 356 Abs. 6 Nr. 2 early-extinguishment clause is the key difference
 * from the generic "physical goods" template).
 *
 * INTERIM TEMPLATE — replace with eRecht24's generated text once available.
 *
 * Nach jeder Änderung: `node scripts/rechtstexte-pdf.mjs` — die Kauf-Mail hängt diese Seite als PDF an.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import PageMeta from '../components/PageMeta';
import RechtsRahmen from '../components/recht/RechtsRahmen';

const STAND = { de: 'Oktober 2026', en: 'October 2026' };

const Widerruf = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en' || location.pathname.startsWith('/en');
  // Elektronische Widerrufsfunktion (Pflicht seit 19.06.2026) — Pfad fest vereinbart
  const widerrufsFunktion = isEnglish ? '/en/withdraw' : '/vertrag-widerrufen';

  return (
    <RechtsRahmen en={isEnglish} titel={isEnglish ? 'Withdrawal Policy' : 'Widerrufsbelehrung'} unterzeile={isEnglish ? 'For consumer contracts under § 312g BGB' : 'Für Verbraucherverträge gemäß § 312g BGB'}>
      <PageMeta
        title={isEnglish ? 'Right of Withdrawal | DAVIDMEDIA' : 'Widerrufsbelehrung | DAVIDMEDIA'}
        description={
          isEnglish
            ? 'Right of withdrawal and cancellation policy for orders placed with DAVIDMEDIA.'
            : 'Widerrufsrecht und Widerrufsbelehrung für Bestellungen bei DAVIDMEDIA.'
        }
        url={`https://www.davidmedia.de${isEnglish ? '/en/withdrawal' : '/widerruf'}`}
      />


          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Widerrufsrecht */}
            <div className="rounded-2xl p-8 liquid-glass">
              <h2 className="font-syne font-bold text-xl mb-4" style={{ color: 'var(--text-primary)' }}>
                {isEnglish ? 'Right of Withdrawal' : 'Widerrufsrecht'}
              </h2>
              <div className="text-sm leading-relaxed space-y-4" style={{ color: 'var(--text-secondary)' }}>
                <p>
                  {isEnglish
                    ? 'You have the right to withdraw from this contract within 14 days without giving any reason.'
                    : 'Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.'}
                </p>
                <p>
                  {isEnglish
                    ? 'The withdrawal period is 14 days from the day on which the contract was concluded.'
                    : 'Die Widerrufsfrist beträgt 14 Tage ab dem Tag des Vertragsabschlusses.'}
                </p>
                <p>
                  {isEnglish
                    ? <>To exercise your right of withdrawal, you must inform us (David Ferfers, Bruchhöfe 21, 47829 Krefeld, Email: <a href="mailto:contact@davidmedia.de" style={{ color: 'var(--primary)' }} className="underline">contact@davidmedia.de</a>) by means of a clear declaration (e.g. a letter sent by post or email) of your decision to withdraw from this contract. You may use the attached model withdrawal form, but it is not obligatory.</>
                    : <>Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (David Ferfers, Bruchhöfe 21, 47829 Krefeld, E-Mail: <a href="mailto:contact@davidmedia.de" style={{ color: 'var(--primary)' }} className="underline">contact@davidmedia.de</a>) mittels einer eindeutigen Erklärung (z. B. ein per Post versandter Brief oder per E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das unten beigefügte Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist.</>}
                </p>
                <p>
                  {isEnglish
                    ? <>You can also declare your withdrawal electronically via the <Link to={widerrufsFunktion} style={{ color: 'var(--primary)' }} className="underline">“Withdraw from contract”</Link> function on our website (www.davidmedia.de/en/withdraw). If you use this option, we will send you an acknowledgement of receipt of your withdrawal by email without delay.</>
                    : <>Sie können Ihren Widerruf auch elektronisch über die Funktion <Link to={widerrufsFunktion} style={{ color: 'var(--primary)' }} className="underline">„Vertrag widerrufen“</Link> auf unserer Website erklären (www.davidmedia.de/vertrag-widerrufen). Machen Sie von dieser Möglichkeit Gebrauch, so übermitteln wir Ihnen unverzüglich per E-Mail eine Bestätigung über den Eingang Ihres Widerrufs.</>}
                  {/* Die Eingangsbestätigung verschickt server.py (POST /api/withdrawal) per E-Mail. */}
                </p>
                <p>
                  {isEnglish
                    ? 'To meet the withdrawal deadline, it is sufficient for you to send your communication concerning your exercise of the right of withdrawal before the withdrawal period has expired.'
                    : 'Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.'}
                </p>
              </div>
            </div>

            {/* Folgen des Widerrufs */}
            <div className="rounded-2xl p-8 liquid-glass">
              <h2 className="font-syne font-bold text-xl mb-4" style={{ color: 'var(--text-primary)' }}>
                {isEnglish ? 'Consequences of Withdrawal' : 'Folgen des Widerrufs'}
              </h2>
              <div className="text-sm leading-relaxed space-y-4" style={{ color: 'var(--text-secondary)' }}>
                <p>
                  {isEnglish
                    ? 'If you withdraw from this contract, we shall reimburse to you all payments received from you without undue delay and in any event not later than 14 days from the day on which we are informed about your decision to withdraw from this contract. We will carry out such reimbursement using the same means of payment as you used for the initial transaction, unless you have expressly agreed otherwise; in any event, you will not incur any fees as a result of such reimbursement.'
                    : 'Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, unverzüglich und spätestens binnen 14 Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist. Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.'}
                </p>
              </div>
            </div>

            {/* Vorzeitiges Erlöschen — KRITISCH für digitale Downloads */}
            <div className="rounded-2xl p-8 liquid-glass" style={{ borderColor: 'rgba(249, 115, 22, 0.3)', border: '1px solid rgba(249, 115, 22, 0.3)' }}>
              <h2 className="font-syne font-bold text-xl mb-4" style={{ color: 'var(--primary)' }}>
                {isEnglish ? 'Early Expiry for Digital Content' : 'Vorzeitiges Erlöschen bei digitalen Inhalten'}
              </h2>
              <div className="text-sm leading-relaxed space-y-4" style={{ color: 'var(--text-secondary)' }}>
                <p>
                  {isEnglish
                    ? 'Pursuant to § 356 (6) no. 2 of the German Civil Code (BGB), the right of withdrawal for contracts for the supply of digital content not delivered on a tangible medium expires if we have begun performance of the contract after you have:'
                    : 'Gemäß § 356 Abs. 6 Nr. 2 BGB erlischt das Widerrufsrecht bei einem Vertrag über die Lieferung von nicht auf einem körperlichen Datenträger befindlichen digitalen Inhalten, wenn wir mit der Ausführung des Vertrags begonnen haben, nachdem Sie:'}
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>{isEnglish ? 'expressly consented to us beginning performance of the contract before the expiry of the withdrawal period, and' : 'ausdrücklich zugestimmt haben, dass wir mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist beginnen, und'}</li>
                  <li>{isEnglish ? 'acknowledged that you will lose your right of withdrawal by giving such consent and through the beginning of performance.' : 'Ihre Kenntnis davon bestätigt haben, dass Sie durch Ihre Zustimmung mit Beginn der Ausführung des Vertrags Ihr Widerrufsrecht verlieren.'}</li>
                </ul>
                <p>
                  {isEnglish
                    ? <strong>Practical effect:</strong>
                    : <strong>Praktische Auswirkung:</strong>}
                  {' '}
                  {isEnglish
                    ? 'Before you are forwarded to payment — from the shopping cart as well as when taking out the All-in-One subscription — we ask you to give this express consent via a separate checkbox. If you tick it, your right of withdrawal expires as soon as we make the license key and download available to you (usually immediately after the order is completed).'
                    : 'Vor der Weiterleitung zur Bezahlung — aus dem Warenkorb ebenso wie beim Abschluss des All-in-One-Abos — bitten wir Sie über ein eigenes Häkchen um diese ausdrückliche Zustimmung. Setzen Sie das Häkchen, erlischt Ihr Widerrufsrecht, sobald wir Ihnen Lizenzschlüssel und Download bereitstellen (in der Regel direkt nach Abschluss der Bestellung).'}
                </p>
              </div>
            </div>

            {/* Abonnement (All-in-One) — der Widerruf laeuft hier anders als beim
                Einmalkauf: das Abo ist ein Dauerschuldverhaeltnis, und die
                sieben Tage Testphase sind kostenfrei. Ohne diesen Abschnitt
                bliebe offen, was ein Widerruf mitten in der Laufzeit kostet. */}
            <div className="rounded-2xl p-8 liquid-glass">
              <h2 className="font-syne font-bold text-xl mb-4" style={{ color: 'var(--text-primary)' }}>
                {isEnglish ? 'Special Rules for the All‑in‑One Subscription' : 'Besonderheiten beim All‑in‑One-Abonnement'}
              </h2>
              <div className="text-sm leading-relaxed space-y-4" style={{ color: 'var(--text-secondary)' }}>
                <p>
                  {isEnglish
                    ? 'The All-in-One subscription (monthly or yearly) is also subject to the 14-day right of withdrawal, counted from the day the contract was concluded. The section “Early Expiry for Digital Content” above applies here as well.'
                    : 'Auch für das All-in-One-Abonnement (monatlich oder jährlich) gilt das Widerrufsrecht von 14 Tagen, gerechnet ab dem Tag des Vertragsschlusses. Der Abschnitt „Vorzeitiges Erlöschen bei digitalen Inhalten“ oben gilt auch hier.'}
                </p>
                <p>
                  {isEnglish
                    ? 'The first seven days are free of charge. If you cancel during this trial period, no payment is due and no reimbursement is necessary.'
                    : 'Die ersten sieben Tage sind kostenfrei. Kündigen Sie innerhalb dieser Testphase, entsteht keine Zahlungspflicht und es ist nichts zu erstatten.'}
                </p>
                {/* PRÜFEN: Dieser Absatz (Wertersatz für den genutzten Anteil) passt zu Dienstleistungen bzw. digitalen
                    Dienstleistungen. Für digitale Inhalte schließt § 357a Abs. 3 BGB Wertersatz aus, und mit dem
                    Häkchen nach § 356 Abs. 6 Nr. 2 BGB erlischt das Widerrufsrecht ohnehin. Rechtlich klären, ob das Abo als
                    digitaler Inhalt gilt — dann diesen Absatz streichen. */}
                <p>
                  {isEnglish
                    ? 'If you have expressly asked us to begin performance during the withdrawal period — that is, if you have used the subscription — and you then withdraw, you owe us a reasonable amount for the portion already used, in proportion to the total agreed price.'
                    : 'Haben Sie ausdrücklich verlangt, dass wir während der Widerrufsfrist mit der Leistung beginnen — nutzen Sie das Abonnement also bereits —, und widerrufen Sie danach, schulden Sie uns einen angemessenen Betrag für den bereits genutzten Anteil, im Verhältnis zum vereinbarten Gesamtpreis.'}
                </p>
                <p>
                  {/* Kündigungsbutton nach § 312k BGB (VertraegeKuendigen.jsx), ohne Anmeldung erreichbar */}
                  {isEnglish
                    ? <>Independently of the right of withdrawal, you can cancel the subscription at any time with effect from the end of the current term — using the <Link to="/en/cancel-contract" style={{ color: 'var(--primary)' }} className="underline">“Cancel contracts here”</Link> button at the bottom of every page (no sign-in required) or in your customer account under “Manage or cancel subscription”, without giving a reason and without a notice period.</>
                    : <>Unabhängig vom Widerrufsrecht können Sie das Abonnement jederzeit zum Ende der laufenden Laufzeit kündigen — über die Schaltfläche <Link to="/vertraege-kuendigen" style={{ color: 'var(--primary)' }} className="underline">„Verträge hier kündigen“</Link> unten auf jeder Seite (ohne Anmeldung) oder in Ihrem Kundenkonto unter „Abo verwalten oder kündigen“, ohne Angabe von Gründen und ohne Kündigungsfrist.</>}
                </p>
              </div>
            </div>

            {/* Muster-Widerrufsformular */}
            <div className="rounded-2xl p-8 liquid-glass">
              <h2 className="font-syne font-bold text-xl mb-4" style={{ color: 'var(--text-primary)' }}>
                {isEnglish ? 'Model Withdrawal Form' : 'Muster-Widerrufsformular'}
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {isEnglish
                  ? '(If you wish to withdraw from the contract, please complete and return this form.)'
                  : '(Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es zurück.)'}
              </p>
              {/* Gleicher Wortlaut wie vorher, aber ohne feste Zeilenumbrüche: Der Text bricht
                  selbst um, die Schreiblinien sind Rahmenlinien (recht.css) statt „____“. */}
              <div
                className="muster-formular text-sm leading-relaxed p-6 rounded-lg font-mono"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'var(--text-secondary)' }}
              >
                <p>
                  {(isEnglish
                    ? ['To:', 'David Ferfers', 'Bruchhöfe 21', '47829 Krefeld', 'Germany', 'contact@davidmedia.de']
                    : ['An:', 'David Ferfers', 'Bruchhöfe 21', '47829 Krefeld', 'contact@davidmedia.de']
                  ).map((zeile, i) => <React.Fragment key={zeile}>{i > 0 && <br />}{zeile}</React.Fragment>)}
                </p>
                <p>
                  {isEnglish
                    ? 'I/We (*) hereby give notice that I/We (*) withdraw from my/our (*) contract of sale of the following goods/services (*):'
                    : 'Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über den Kauf der folgenden Waren / die Erbringung der folgenden Dienstleistung (*):'}
                </p>
                <span className="muster-linie" aria-hidden="true" />
                {(isEnglish
                  ? ['Ordered on (*) / received on (*):', 'Name of consumer(s):', 'Address of consumer(s):']
                  : ['Bestellt am (*) / erhalten am (*):', 'Name des/der Verbraucher(s):', 'Anschrift des/der Verbraucher(s):']
                ).map(feld => (
                  <p key={feld} className="muster-feld">{feld}<span className="muster-linie" aria-hidden="true" /></p>
                ))}
                <p>
                  {isEnglish
                    ? 'Signature of consumer(s) (only if this form is notified on paper):'
                    : 'Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier):'}
                </p>
                <span className="muster-linie kurz" aria-hidden="true" />
                <p className="muster-feld">{isEnglish ? 'Date:' : 'Datum:'}<span className="muster-linie" aria-hidden="true" /></p>
                <p>{isEnglish ? '(*) Delete as applicable.' : '(*) Unzutreffendes streichen.'}</p>
              </div>
            </div>

            {/* Stand */}
            <div className="text-center pt-4">
              <p className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
                {isEnglish ? 'Last updated: ' : 'Stand: '}
                {isEnglish ? STAND.en : STAND.de}
              </p>
            </div>
          </motion.div>
    </RechtsRahmen>
  );
};

export default Widerruf;
